import * as React from 'react';
import {
    DataCollectionAiAgentOperationState,
    DataCollectionAiAgentOperationCode
} from '../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels';
import {
    isTerminatedAiOperationState,
    parseAiOperationCodeValue
} from '../../../store/StateControllers/Survey/SurveyResponse/AISurveyAgentController';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { localizeAiAgentState, useLocalizer, useLocalizerFormat } from './hooks/useLocalizer';

const stageActiveIconSrc = "/images/supplier-ai-agent/stage-active-icon.svg";
const stageCompletedIconSrc = "/images/supplier-ai-agent/stage-completed-icon.svg";
const stagePendingIconSrc = "/images/supplier-ai-agent/stage-pending-icon.svg";

/**
 * Gets the display message for the current AI processing stage based on the workflow sequence:
 * 
 * State 0 (Triggered): "Initializing AI Workflow..."
 * State 1 (RequestInProgress): Shows operation-specific message with progress
 *   - OpCode 0: "Reading Survey Questions"
 *   - OpCode 1: "Reading Documents (X of Y)"
 *   - OpCode 2: "Consulting Regulatory Agent"
 *   - OpCode 3: "Generating Answer Suggestions (X of Y)"
 * State 2 (ResponseReceived): "Response received from AI Agent. Processing..."
 * State 9 (PreparingGapSummary): "Preparing gap summary..."
 * State 3 (ResponseProcessed): "Success (X of Y questions answered)"
 * State 4 (Failed): "Failed (error details)"
 */
const getStageMessage = (
    aiState: DataCollectionAiAgentOperationState | null,
    agentOperationCode: DataCollectionAiAgentOperationCode | null,
    localizedAgentState: string,
    queueTasksAhead: number | null, 
    localize: (key: string) => string,
    localizeFormat: (formatString: string, ...args: any[]) => string
): React.ReactNode => {
    switch (aiState) {
        case DataCollectionAiAgentOperationState.Triggered:
            if (queueTasksAhead !== null && queueTasksAhead !== undefined && queueTasksAhead > 0) {
                return (
                    <>
                        {localize("Initializing AI Workflow")}
                        <br />
                        <span style={{ color: '#ff0000' }}>
                            {localizeFormat("Processing your request - {0} task(s) ahead in queue.", queueTasksAhead)}
                        </span>
                    </>
                );
            }
            return localize("Initializing AI Workflow");
            
        case DataCollectionAiAgentOperationState.RequestInProgress:
            return getRequestInProgressMessage(agentOperationCode, localizedAgentState, localize, localizeFormat);
            
        case DataCollectionAiAgentOperationState.ResponseReceived:
            return localize("Response received from AI Agent. Processing...");

        case DataCollectionAiAgentOperationState.PreparingGapSummary:
            return localize("Preparing gap summary...");
            
        case DataCollectionAiAgentOperationState.ResponseProcessed:
            if (localizedAgentState.length > 0) {
                return localizeFormat("Success ({0})", localizedAgentState);
            }
            return localize("Success");

        case DataCollectionAiAgentOperationState.DeleteInProgress:
            if (localizedAgentState.length > 0) {
                return localizeFormat("Deleting your AI answers ({0})", localizedAgentState);
            }
            return localize("Deleting your AI answers");
            
        case DataCollectionAiAgentOperationState.Failed:
        case DataCollectionAiAgentOperationState.TerminatedByExchange:
        case DataCollectionAiAgentOperationState.TerminatedByUser:
            if (localizedAgentState.length > 0) {
                return localizeFormat("Failed ({0})", localizedAgentState);
            }
            return localize("Failed");
            
        default:
            return localize("Initializing AI Workflow");
    }
};

/**
 * Gets the message for RequestInProgress state based on operation code
 */
const getRequestInProgressMessage = (
    agentOperationCode: DataCollectionAiAgentOperationCode | null,
    localizedAgentState: string,
    localize: (key: string) => string,
    localizeFormat: (formatString: string, ...args: any[]) => string
): string => {
    const opCode = parseAiOperationCodeValue(agentOperationCode);

    if (opCode === null) {
        return localize("Request sent to AI Agent");
    }

    switch (opCode) {
        case DataCollectionAiAgentOperationCode.ReadingSurveyQuestions:
            return localize("Reading Survey Questions");
            
        case DataCollectionAiAgentOperationCode.ReadingDocuments:
            if (localizedAgentState.length > 0) {
                return localizeFormat("Reading Documents ({0})", localizedAgentState);
            }
            return localize("Reading Documents");
            
        case DataCollectionAiAgentOperationCode.ConsultingRegulatoryDatabase:
            return localize("Consulting Regulatory Agent");
            
        case DataCollectionAiAgentOperationCode.GeneratingAnswerSuggestions:
            if (localizedAgentState.length > 0) {
                return localizeFormat("Generating Answer Suggestions ({0})", localizedAgentState);
            }
            return localize("Generating Answer Suggestions");
            
        default:
            return localize("Request sent to AI Agent");
    }
};

/**
 * Gets all processing stages for the workflow visualization
 */
const getProcessingStages = (
    aiState: DataCollectionAiAgentOperationState | null,
    agentOperationCode: DataCollectionAiAgentOperationCode | null,
    localizedAgentState: string,
    queueTasksAhead: number | null,
    localize: (key: string) => string,
    localizeFormat: (formatString: string, ...args: any[]) => string
): React.ReactNode[] => {
    const isDeleteFlowState = aiState === DataCollectionAiAgentOperationState.DeleteInProgress;

    if (isDeleteFlowState) {
        const deleteStages: React.ReactNode[] = [
            localize("Deleting your AI answers")
        ];

        const deleteStageIndex = getStageIndexForState(aiState, agentOperationCode);
        if (deleteStageIndex >= 0 && deleteStageIndex < deleteStages.length) {
            deleteStages[deleteStageIndex] = getStageMessage(
                aiState,
                agentOperationCode,
                localizedAgentState,
                queueTasksAhead,
                localize,
                localizeFormat
            );
        }

        return deleteStages;
    }

    // Define the stages based on the workflow sequence
    const stages: React.ReactNode[] = [
        localize("Initializing AI Workflow"),                    // state = 0
        localize("Request sent to AI Agent"),                    // state = 1 (initial)
        localize("Reading Survey Questions"),                       // state = 1, opCode = 0
        localize("Reading Documents"),                              // state = 1, opCode = 1
        localize("Consulting Regulatory Agent"),                    // state = 1, opCode = 2
        localize("Generating Answer Suggestions"),                  // state = 1, opCode = 3
        localize("Response received from AI Agent. Processing..."), // state = 2
        localize("Preparing gap summary..."),                       // state = 9
    ];
    
    // Update the current stage message with progress info if available
    const currentStageIndex = getStageIndexForState(aiState, agentOperationCode);
    if (currentStageIndex >= 0 && currentStageIndex < stages.length) {
        stages[currentStageIndex] = getStageMessage(
            aiState,
            agentOperationCode,
            localizedAgentState,
            queueTasksAhead,
            localize,
            localizeFormat
        );
    }
    
    return stages;
};

/**
 * Map backend states and operation codes to stage indices for progress indication
 */
const getStageIndexForState = (aiState: DataCollectionAiAgentOperationState | null, agentOperationCode: DataCollectionAiAgentOperationCode | null = null): number => {
    switch (aiState) {
        case DataCollectionAiAgentOperationState.Triggered:
            return 0; // "Initializing AI Workflow..."
            
        case DataCollectionAiAgentOperationState.RequestInProgress:
            // Map operation code to specific stage
            const opCode = parseAiOperationCodeValue(agentOperationCode);
            if (opCode === null) {
                return 1; // "Request sent to AI Agent..."
            }

            switch (opCode) {
                case DataCollectionAiAgentOperationCode.ReadingSurveyQuestions:
                    return 2;
                case DataCollectionAiAgentOperationCode.ReadingDocuments:
                    return 3;
                case DataCollectionAiAgentOperationCode.ConsultingRegulatoryDatabase:
                    return 4;
                case DataCollectionAiAgentOperationCode.GeneratingAnswerSuggestions:
                    return 5;
                default:
                    return 1;
            }
            
        case DataCollectionAiAgentOperationState.ResponseReceived:
            return 6; // "Response received from AI Agent. Processing..."

        case DataCollectionAiAgentOperationState.PreparingGapSummary:
            return 7; // "Preparing gap summary..."
            
        case DataCollectionAiAgentOperationState.ResponseProcessed:
            return 8; // Complete

        case DataCollectionAiAgentOperationState.DeleteInProgress:
            return 0;
            
        case DataCollectionAiAgentOperationState.Failed:
        case DataCollectionAiAgentOperationState.TerminatedByExchange:
        case DataCollectionAiAgentOperationState.TerminatedByUser:
            return -1; // Special state for failure
            
        default:
            return 0;
    }
};

interface AIProcessingStagesProps {
    currentStage: number;
    aiState?: DataCollectionAiAgentOperationState | null;
    agentOperationCode?: DataCollectionAiAgentOperationCode | null;
    agentState?: string | null;
    queueTasksAhead?: number | null;
    localizer?: IStringLocalizer;
}

export const AIProcessingStages: React.FC<AIProcessingStagesProps> = ({
    currentStage,
    aiState = null,
    agentOperationCode = null,
    agentState = null,
    queueTasksAhead = null,
    localizer
}) => {
    const localize = useLocalizer(localizer);
    const localizeFormat = useLocalizerFormat(localizer);
    const localizedAgentState = localizeAiAgentState(agentState, localize, localizeFormat);
    const processingStages = getProcessingStages(
        aiState,
        agentOperationCode,
        localizedAgentState,
        queueTasksAhead,
        localize,
        localizeFormat
    );
    const isDeleteFlowState = aiState === DataCollectionAiAgentOperationState.DeleteInProgress;
    
    // Use backend state to determine current stage if available, otherwise use prop
    const backendStageIndex = getStageIndexForState(aiState, agentOperationCode);
    const effectiveCurrentStage = aiState !== null ? backendStageIndex : currentStage;
    
    // Handle failure state - let the parent component handle failure display
    if (aiState === DataCollectionAiAgentOperationState.Failed || isTerminatedAiOperationState(aiState)) {
        return null; // Don't show processing stages when failed
    }

    // Implement rolling window logic - always show 5 stages
    let visibleStartIndex = -1;

    if (effectiveCurrentStage < 0) {
        // Failure or error state
        visibleStartIndex = 0;
    } else if (effectiveCurrentStage < 5) {
        // For stages 0-4, show stages 0-4 (first 5 stages)
        visibleStartIndex = 0;
    } else {
        // For stage 5 and beyond, use rolling window
        visibleStartIndex = effectiveCurrentStage - 4;
    }

    const visibleEndIndex = Math.min(processingStages.length, visibleStartIndex + 5);
    const visibleStages = processingStages.slice(visibleStartIndex, visibleEndIndex);

    if (!isDeleteFlowState) {
        while (visibleStages.length < 5) {
            visibleStages.push('');
        }
    }

    const getStageIcon = (actualIndex: number) => {
        if (actualIndex < effectiveCurrentStage) {
            // Completed - green checkmark
            return <img src={stageCompletedIconSrc} width="18" height="18" className="stage-icon completed" aria-hidden="true" alt="" />;
        } else if (actualIndex === effectiveCurrentStage) {
            // Active - spinning circle
            return <img src={stageActiveIconSrc} width="18" height="18" className="stage-icon active" aria-hidden="true" alt="" />;
        } else {
            // Pending - empty gray circle
            return <img src={stagePendingIconSrc} width="18" height="18" className="stage-icon pending" aria-hidden="true" alt="" />;
        }
    };

    return (
        <div className="processing-stages">
            {visibleStages.map((stageText, visibleIndex) => {
                const actualIndex = visibleStartIndex + visibleIndex;
                const isCompleted = actualIndex < effectiveCurrentStage;
                const isActive = actualIndex === effectiveCurrentStage;

                return (
                    <div
                        key={actualIndex}
                        className={`processing-stage ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''}`}
                    >
                        <div className="stage-icon-wrapper">
                            {getStageIcon(actualIndex)}
                        </div>
                        <span className="stage-text">{stageText}</span>
                    </div>
                );
            })}
        </div>
    );
};

export default AIProcessingStages;
